import { uuid } from '@de/shared/utils';

/**
 * Correlation IDs that flow through the entry-point event sequence.
 *
 * Per Section 11 of the Dynamic Experience Eventing Requirements
 * (Confluence 102190022659), four IDs participate in the chain:
 *
 * - `correlationId`            — App-level identifier carried into lead/contact submission.
 * - `elementLoadedCorrelationId` — Generated on `ElementLoaded`. Becomes
 *                                 `customDimensions.sourceCorrelationId` on every
 *                                 entry-point Product/Form event.
 * - `viewCorrelationId`        — Generated on `ProductLoaded`. Shared by
 *                                 `ProductLoaded`/`ProductViewed`/`ProductClicked`
 *                                 for a single visual instance.
 * - `formViewedCorrelationId`  — Generated on `FormViewed` (or seeded from a URL
 *                                 query param of the same name). Used as
 *                                 `correlationId` on Form/Field events.
 */
export interface CorrelationIds {
  correlationId: string;
  elementLoadedCorrelationId: string;
  viewCorrelationId: string;
  formViewedCorrelationId: string;
}

export interface CorrelationIdSeeds {
  correlationId?: string;
  elementLoadedCorrelationId?: string;
  formViewedCorrelationId?: string;
  /** Override window lookup (used by tests). */
  search?: string;
}

const URL_PARAM_FORM_VIEWED = 'formViewedCorrelationId';

/** Read `formViewedCorrelationId` from a search-string query param, if present. */
const readFormViewedFromUrl = (search?: string): string | null => {
  const source =
    search ?? (typeof window !== 'undefined' ? window.location.search : '');
  if (!source) return null;
  const params = new URLSearchParams(source);
  return params.get(URL_PARAM_FORM_VIEWED);
};

/**
 * Generate or accept-as-seed the correlation IDs needed by the entry-point
 * event sequence. `formViewedCorrelationId` is preserved from the URL when
 * present, so cross-page navigations can keep a single funnel correlated.
 */
export const generateCorrelationIds = (
  seeds: CorrelationIdSeeds = {},
): CorrelationIds => {
  const formViewedFromUrl = readFormViewedFromUrl(seeds.search);
  return {
    correlationId: seeds.correlationId ?? uuid(),
    elementLoadedCorrelationId: seeds.elementLoadedCorrelationId ?? uuid(),
    viewCorrelationId: uuid(),
    formViewedCorrelationId:
      seeds.formViewedCorrelationId ?? formViewedFromUrl ?? uuid(),
  };
};
